import {ReactNode} from 'react';

import {RowData, RowProperties} from 'notebook/tables/constants/RowConstants';
import {TABLE_ROW, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {UNAVAILABLE_TABLE_INTERACTIONS} from 'notebook/tables/store/logic/interactions/createTableInteractions';
import {UNAVAILABLE_TABLE_MUTATIONS} from 'notebook/tables/store/logic/mutations/createTableMutations';
import {computeCustomRowLabel} from 'notebook/tables/store/logic/TableIdentityLogic';
import {computeCanDeleteSelectedRows} from 'notebook/tables/store/logic/TableLockingLogic';
import {
  AxisCellAffixedLayout,
  AxisHeaderModel,
  createAxisHeaderIdentitySelector,
  createAxisIsProtectedSelector,
  createAxisIsSelectedSelector,
  createAxisIsTableSelectedSelector,
  createAxisSelectionRangeSelector,
  createAxisShowExcessLabelsSelector,
  UNAFFIXED_LAYOUT,
} from 'notebook/tables/store/models/AxisHeaderModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createRowStatusSelector} from 'notebook/tables/store/selectors/rowModel/RowStatusSelectors';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * The model for a single row header cell, keyed by row index. Rendered by `TableRow`.
 *
 * The row axis carries none of the column header's constraint-driven metadata (no rename, no
 * column type icon, no custom column highlight), so beyond the shared {@link AxisHeaderModel} this
 * holds the row axis's own permissions and the row metadata its cell renders. Row-specific values
 * that `TableRow` still passes to `TableAxisCell` as props — the row's in-view state — belong here
 * as they are migrated.
 */
export interface RowHeaderModel extends AxisHeaderModel {
  dim: TableDimension.ROW;
  /**
   * Whether the table may be left with no rows, which is what allows the whole row selection to be
   * deleted. Only the row axis can empty the table this way, so this has no column-axis
   * counterpart.
   */
  allowEmpty: boolean;
  /**
   * The row's data, or null when the table has none for this row (for example a plain table, or a
   * row header rendered outside of a table). Its user annotation indicator is rendered in the cell.
   */
  rowData: RowData | null;
  /**
   * The row's status — errors, warnings, staleness, archival and submission state — which drives
   * the cell's status styling and tooltip. Null for a row header rendered outside of a table, which
   * has no row to take a status from.
   */
  rowProperties: RowProperties | null;
}

/**
 * Creates a selector for a row header's {@link AxisCellAffixedLayout}. The row header is the
 * table's leading column, so it is always affixed at the left edge with no columns before it, and
 * it takes over casting the shadow over the scrolled content when no data column is frozen ahead
 * of it. That is the same for every row, so this takes no row index.
 */
function createRowHeaderAffixedLayoutSelector(
  tableSelector: (store: TableEditableStore) => TableModel
): (store: TableEditableStore) => AxisCellAffixedLayout {
  return createTableStoreSelector(
    'rowHeaderAffixedLayout',
    {
      affixedColumnCount: (store) => tableSelector(store).affixed.affixedColumnCount,
      showAffixedShadow: (store) => tableSelector(store).affixed.showAffixedShadow,
      firstColumnWidth: (store) => tableSelector(store).measurements.firstColumnWidth,
    },
    ({affixedColumnCount, showAffixedShadow, firstColumnWidth}): AxisCellAffixedLayout => ({
      isAffixed: true,
      affixedLeftSpacing: 0,
      showAffixedShadow: showAffixedShadow && affixedColumnCount === 0,
      isLastFrozenColumn: false,
      isFirstColumn: firstColumnWidth > 0,
    })
  );
}

/** Creates a selector for the row header at `index` against the table store. */
export function createRowHeaderModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => RowHeaderModel {
  return createTableStoreSelector(
    'rowHeaderModel',
    {
      identity: createAxisHeaderIdentitySelector(tableSelector, TABLE_ROW, index),
      selectionRange: createAxisSelectionRangeSelector(tableSelector, TABLE_ROW, index),
      isSelected: createAxisIsSelectedSelector(tableSelector, TABLE_ROW, index),
      isTableSelected: createAxisIsTableSelectedSelector(tableSelector, TABLE_ROW),
      affixedLayout: createRowHeaderAffixedLayoutSelector(tableSelector),
      showExcessLabels: createAxisShowExcessLabelsSelector(TABLE_ROW),
      customLabel: (store) => computeCustomRowLabel(store.volatileProps.table.rowLabels, index),
      canModifyDims: (store) => tableSelector(store).dimensions.canModifyDims,
      // `props` holds TableEditable's raw props, so the `??` default here repeats the one
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      disableLegacyHeader: (store) => store.stableProps.disableLegacyHeader ?? false,
      canModifyRowDim: (store) => tableSelector(store).dimensions.canModifyRowDim,
      canAddRows: (store) => tableSelector(store).dimensions.canAddRows,
      // The single-input view renders no row headers, so the `!isSingleInput` gate that
      // `TableEditable._getTRProps` applied to row deletion cannot be reached from here.
      canRemoveRows: (store) => tableSelector(store).dimensions.canRemoveRows,
      canDeleteTable: (store) => tableSelector(store).canDeleteTable,
      allowEmpty: (store) => tableSelector(store).dimensions.allowNoRows,
      isProtected: createAxisIsProtectedSelector(tableSelector),
      isTableLocked: (store) => tableSelector(store).locking.isTableLocked,
      lockedRowIndices: (store) => tableSelector(store).locking.lockedRowIndices,
      // Rows are deleted as a whole selection, so this is the selected row range rather than this
      // row's own range: a row outside the row selection has nothing to delete.
      selectedRowRange: (store) => tableSelector(store).selection.multiAxisDimSelection.row,
      rowData: (store) => store.volatileProps.table.rowData?.[index] ?? null,
      // The same status the row itself renders from, so that the row header and its row cannot
      // disagree about whether the row has errors, is archived, and so on.
      rowProperties: createRowStatusSelector(tableSelector, index),
      // `props` holds TableEditable's raw props, so the `??` default here repeats the one
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      isLeftAlign: (store) => store.stableProps.isLeftAlignRowHeader ?? false,
      shouldShowRowDragHandles: (store) => tableSelector(store).reordering.shouldShowRowDragHandles,
      mutations: (store) => tableSelector(store).mutations,
      interactions: (store) => tableSelector(store).interactions,
    },
    ({
      identity,
      selectionRange,
      isSelected,
      isTableSelected,
      affixedLayout,
      showExcessLabels,
      customLabel,
      canModifyDims,
      disableLegacyHeader,
      canModifyRowDim,
      canAddRows,
      canRemoveRows,
      canDeleteTable,
      allowEmpty,
      isProtected,
      isTableLocked,
      lockedRowIndices,
      selectedRowRange,
      rowData,
      rowProperties,
      isLeftAlign,
      shouldShowRowDragHandles,
      mutations,
      interactions,
    }): RowHeaderModel => ({
      ...identity,
      dim: TABLE_ROW,
      isHeaderCell: false,
      isLeftAlign,
      showDragHandle: !identity.isGhostCell && shouldShowRowDragHandles,
      customLabel,
      selectionRange,
      isSelected,
      isTableSelected,
      affixedLayout,
      shouldDisableDeleteDim: !computeCanDeleteSelectedRows(lockedRowIndices, selectedRowRange),
      // Rows can always be inserted around a selected row; whether the insert options are offered
      // at all is decided by `canInsertDims`.
      shouldDisableInsertDimBefore: false,
      shouldDisableInsertDimAfter: false,
      isLocked: isTableLocked,
      isProtected,
      showExcessLabels,
      canToggleHeader: index === 0 && canModifyDims && !disableLegacyHeader,
      canInsertDims: canModifyRowDim && canAddRows,
      canDeleteDims: canRemoveRows,
      canDeleteTable,
      allowEmpty,
      rowData,
      rowProperties,
      mutations,
      interactions,
    })
  );
}

/**
 * A {@link RowHeaderModel} for a row header rendered outside of any table store, whose label comes
 * directly from its props rather than from a table. Such a header labels no table row, so it
 * permits none of the dimension actions, is never selected, and has no row data or status.
 */
export function makeStandaloneRowHeaderModel({
  index,
  customLabel = null,
  numDims = 0,
  isLeftAlign = false,
}: {
  index: number;
  customLabel?: ReactNode;
  numDims?: number;
  isLeftAlign?: boolean;
}): RowHeaderModel {
  return {
    dim: TABLE_ROW,
    index,
    isGhostCell: false,
    numDims,
    isHeaderCell: false,
    isLeftAlign,
    showDragHandle: false,
    customLabel,
    selectionRange: null,
    isSelected: false,
    isTableSelected: false,
    affixedLayout: UNAFFIXED_LAYOUT,
    shouldDisableDeleteDim: true,
    shouldDisableInsertDimBefore: true,
    shouldDisableInsertDimAfter: true,
    isLocked: false,
    isProtected: false,
    showExcessLabels: false,
    canToggleHeader: false,
    canInsertDims: false,
    canDeleteDims: false,
    canDeleteTable: false,
    allowEmpty: false,
    rowData: null,
    rowProperties: null,
    mutations: UNAVAILABLE_TABLE_MUTATIONS,
    interactions: UNAVAILABLE_TABLE_INTERACTIONS,
  };
}
